import { FollowUp } from '@app/modules/follow-ups/follow-ups.types';
import { ExtendedKit, KitExtensions } from '@app/modules/kits/kits.types';
import { CancerscreenSampleServiceInformation } from '@app/modules/service-data/types/cancerscreen.types';
import { CarrierSampleServiceInformation } from '@app/modules/service-data/types/carrier.types';
import { GalleriSampleServiceInformation } from '@app/modules/service-data/types/galleri.types';
import { MonoSampleServiceInformation } from '@app/modules/service-data/types/mono.types';
import { NegSingleCarrierSampleServiceInformation } from '@app/modules/service-data/types/neg-single-carrier.types';
import { NipptSampleServiceInformation } from '@app/modules/service-data/types/nippt.types';
import { NiptSampleServiceInformation } from '@app/modules/service-data/types/nipt.types';
import { PaternitySampleServiceInformation } from '@app/modules/service-data/types/paternity.types';
import { RhdSampleServiceInformation } from '@app/modules/service-data/types/rhd.types';
import { VenisafeSampleServiceInformation } from '@app/modules/service-data/types/venisafe.types';
import { AwbAddress, Partner, Product } from '@core/core.types';
import { OtherSampleServiceInformation } from './other.types';
import {
  ExtendedServiceData,
  GestationAge,
  ServiceDataExtensions,
} from './service-data.types';

export interface Sample {
  id: string;
  partnerId: string;
  serviceDataId: string;
  productId: string;
  kitId?: string;
  previousSampleId?: string;
  nextSampleId?: string;
  productInventoryItems?: string[];
  followUpIds?: string[];
  dateOfVenipuncture: Date;
  shippingInformation: ShippingInformation;
  serviceInformation: SampleServiceInformation;
  notes: string;
  laboratory?: string;
  generated?: Date;
  forResample?: Date;
  redrawn?: Date;
  finished?: Date;
  status?: string;
  statusTimestamp?: Date;
}

export interface ExtendedServiceInformation extends SampleServiceInformation {
  shared?: {
    sampleType: SampleType;
    doctorId: string;
    gestationAge: GestationAge;
    isGestationAgeAutomaticallyCalculated: boolean;
    ultrasoundDate: Date;
  };
}

export interface SharedSample extends Sample {
  serviceInformation: ExtendedServiceInformation;
}

export type ProvisionSample = Omit<
  Sample,
  'status' | 'statusTimestamp' | 'productInventoryItems'
>;

export interface ShippingInformation {
  waybill: string;
  documentId?: string;
  address?: AwbAddress;
}
export interface SampleServiceInformation {
  nipt?: NiptSampleServiceInformation;
  nippt?: NipptSampleServiceInformation;
  rhd?: RhdSampleServiceInformation;
  carrier?: CarrierSampleServiceInformation;
  mono?: MonoSampleServiceInformation;
  venisafe?: VenisafeSampleServiceInformation;
  cancerScreen?: CancerscreenSampleServiceInformation;
  galleri?: GalleriSampleServiceInformation;
  paternity?: PaternitySampleServiceInformation;
  negSingleCarrier?: NegSingleCarrierSampleServiceInformation;
  other?: OtherSampleServiceInformation;
}

export interface ExtendedSample extends Sample {
  partner?: Partner;
  doctor?: Partner;
  product?: Product;
  serviceData?: ExtendedServiceData;
  kit?: ExtendedKit;
  followUps?: FollowUp[];
}

export enum SampleType {
  Saliva = 'S079',
  Blood = 'S051',
  Fingernails = 'Fingernails',
  Hairs = 'Hairs',
  Other = 'Other',
}

// Sample types are translated by their enum key (sample.form.sampleTypes.*),
// not their code
export function sampleTypeKey(sampleType: string): string {
  return Object.keys(SampleType).find((key) => SampleType[key] === sampleType);
}

export enum SampleStatus {
  generated = 'Generated',
  forResample = 'ForResample',
  redrawn = 'Redrawn',
  finished = 'Finished',
}

export interface SampleExtensions {
  partner?: boolean;
  doctor?: boolean;
  product?: boolean;
  kit?: KitExtensions;
  serviceData?: ServiceDataExtensions;
  followUps?: boolean;
}
